import { useState, type ReactNode } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Plus } from 'lucide-react'
import { Button, Input, Label, Modal } from '@atrvd/saas-ui'
import type { BankAccountRow, Facility } from '@/types/banking'
import {
  ACCOUNT_TYPE_CHOICES,
  accountTypeOptions,
} from '@/components/ledgersync/connectionManagement.utils'
import { ManualAccountRowFields } from './ManualAccountRowFields'
import { RequiredMark } from '@/components/ui/required-mark'
import {
  BLANK_ACCOUNT_ROW,
  accountFormValues,
  manualBankSchema,
  type ManualBankFormMode,
  type ManualBankFormValues,
} from './manualBankSchema'
import {
  MANUAL_BANK_COPY,
  bankFormSubmitLabel,
  bankFormTitle,
  duplicateInstitutionMessage,
} from './manualBankCopy'
import { useSaveManualBank } from './useSaveManualBank'
import { isRename, type SeededManualAccount } from './manualBankSave'
import {
  facilityOptionsFor,
  isAccountType,
  isDuplicateInstitutionName,
} from './manualBanks.utils'

/** The `id` the dialog's footer submit button targets. */
const MANUAL_BANK_FORM_ID = 'manual-bank-form'

interface ManualBankFormModalProps {
  mode: ManualBankFormMode
  /** Every bank account of the organization. */
  accounts: BankAccountRow[]
  /** Every facility, inactive included. */
  facilities: Facility[]
  /**
   * The account list is refetching: the duplicate check would run against a
   * list about to change, so saving waits.
   */
  refreshing: boolean
  onClose: () => void
  onSaved?: (institutionName: string) => void
}

/**
 * The manual bank form in its modal. Mount it once its data has loaded: it
 * seeds exactly once, so a background refetch never resets what the user typed.
 *
 * Validation follows the project's form default: errors appear when the user
 * tries to save (nothing is written) and clear as each field is fixed; the
 * first invalid field — input or select — takes focus, which scrolls it into
 * view in the modal body (a seeded row's empty Facility can sit below the fold). The
 * primary button is disabled only while saving or while the form cannot work
 * (account list refetching, no active facility) — never for validity.
 *
 * In edit mode it seeds EVERY account of the bank, those under a deactivated
 * facility included (ADR 0016 hides them from the lists, not from the bank):
 * the bank-wide edit acts on the whole bank, like its Delete. Such a row's
 * Facility picker keeps its inactive facility, so saving leaves it assigned.
 */
export function ManualBankFormModal({
  mode,
  accounts,
  facilities,
  refreshing,
  onClose,
  onSaved,
}: ManualBankFormModalProps) {
  const { save } = useSaveManualBank(mode, { onClose, onSaved })
  // Snapshot of the bank's accounts at seeding: what the save diffs against,
  // and what keeps a legacy type offered on its row.
  const [seeded] = useState<SeededManualAccount[]>(() =>
    mode.kind === 'edit'
      ? accounts
          .filter((a) => !a.integration_id && a.institution_name === mode.institutionName)
          .map((a) => ({ id: a.id, ...accountFormValues(a) }))
      : [],
  )

  // New rows start at the page's facility when it can be picked (an active
  // one); an inactive facility's page is read-only and never opens the form.
  const defaultFacilityId = mode.kind === 'edit' ? null : mode.defaultFacilityId
  const blankRow = {
    ...BLANK_ACCOUNT_ROW,
    facility_id:
      defaultFacilityId && facilities.some((f) => f.id === defaultFacilityId && f.is_active)
        ? defaultFacilityId
        : BLANK_ACCOUNT_ROW.facility_id,
  }

  const {
    control,
    register,
    handleSubmit,
    getValues,
    setError,
    // Spans the whole awaited submit, i.e. every write of the save: the dialog
    // must stay inert (no close, no second submit) until all of them settle.
    formState: { errors, isSubmitting: saving },
  } = useForm<ManualBankFormValues>({
    resolver: zodResolver(manualBankSchema),
    defaultValues: {
      institution_name: mode.kind === 'create' ? mode.initialName : mode.institutionName,
      accounts: seeded.length > 0 ? seeded : [{ ...blankRow }],
    },
  })
  // `fields[i].id` is the field array's own render key, not the account id, so
  // a row's account id is read from the form values instead.
  const { fields, append, remove } = useFieldArray({ control, name: 'accounts' })

  const seededById = new Map(seeded.map((row) => [row.id, row]))
  const canAddRows = facilities.some((f) => f.is_active)

  const submit = handleSubmit(async (values) => {
    const name = values.institution_name
    const duplicate =
      mode.kind === 'create'
        ? isDuplicateInstitutionName(accounts, name, null)
        : mode.kind === 'edit' &&
          isRename(mode.institutionName, name) &&
          isDuplicateInstitutionName(accounts, name, mode.institutionName)
    if (duplicate) {
      setError(
        'institution_name',
        { type: 'duplicate', message: duplicateInstitutionMessage(name) },
        { shouldFocus: true },
      )
      return
    }
    await save(values, seeded)
  })

  return (
    <ManualBankModalShell
      mode={mode}
      onClose={onClose}
      saving={saving}
      submitDisabled={refreshing || !canAddRows}
    >
      <form id={MANUAL_BANK_FORM_ID} onSubmit={submit} className="space-y-6" noValidate>
        {!canAddRows && (
          <div
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
          >
            {MANUAL_BANK_COPY.noActiveFacilities}
          </div>
        )}

        {mode.kind !== 'add-accounts' && (
          <div className="space-y-2">
            <Label htmlFor={`${MANUAL_BANK_FORM_ID}-institution`}>
              {MANUAL_BANK_COPY.institutionLabel}
              <RequiredMark />
            </Label>
            <Input
              id={`${MANUAL_BANK_FORM_ID}-institution`}
              placeholder={MANUAL_BANK_COPY.institutionPlaceholder}
              aria-required="true"
              className="aria-[invalid=true]:border-destructive"
              aria-invalid={!!errors.institution_name}
              aria-describedby={
                errors.institution_name ? `${MANUAL_BANK_FORM_ID}-institution-error` : undefined
              }
              {...register('institution_name')}
            />
            {errors.institution_name && (
              <p id={`${MANUAL_BANK_FORM_ID}-institution-error`} className="text-sm text-destructive">
                {errors.institution_name.message}
              </p>
            )}
          </div>
        )}

        <div className="space-y-3">
          <p className="text-[15px] font-bold">{MANUAL_BANK_COPY.accountsSectionLabel}</p>
          {/* No own overflow here: the Modal body is already the scroll container. */}
          <div className="space-y-3">
            {fields.map((field, index) => {
              const accountId = getValues(`accounts.${index}.id`)
              const stored = accountId ? seededById.get(accountId) : undefined
              return (
                <ManualAccountRowFields
                  key={field.id}
                  formId={MANUAL_BANK_FORM_ID}
                  index={index}
                  control={control}
                  register={register}
                  errors={errors}
                  typeOptions={rowTypeOptions(stored?.account_type)}
                  facilityOptions={facilityOptionsFor(facilities, stored?.facility_id || null)}
                  canRemove={fields.length > 1}
                  onRemove={() => remove(index)}
                />
              )
            })}
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => append({ ...blankRow })}
            disabled={!canAddRows}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {MANUAL_BANK_COPY.addAccountCta}
          </Button>
        </div>
      </form>
    </ManualBankModalShell>
  )
}

/**
 * Type options for a row: the standard five, plus the stored type of an
 * existing row when it is not among them (legacy `line_of_credit`), so saving
 * without touching Type keeps it. New rows never get it.
 */
function rowTypeOptions(storedType: string | undefined) {
  return isAccountType(storedType) ? accountTypeOptions(storedType) : ACCOUNT_TYPE_CHOICES
}

interface ManualBankModalShellProps {
  mode: ManualBankFormMode
  onClose: () => void
  saving: boolean
  submitDisabled: boolean
  children: ReactNode
}

/**
 * The bank form's modal frame: title and the Discard / primary footer. Shared
 * by the loading state and the form so both look the same.
 */
export function ManualBankModalShell({
  mode,
  onClose,
  saving,
  submitDisabled,
  children,
}: ManualBankModalShellProps) {
  return (
    <Modal
      isOpen
      onClose={() => {
        if (!saving) onClose()
      }}
      title={bankFormTitle(mode)}
      size="lg"
      className="!max-w-[860px]"
      footer={
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Discard
          </Button>
          <Button type="submit" form={MANUAL_BANK_FORM_ID} disabled={saving || submitDisabled}>
            {saving && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
            {bankFormSubmitLabel(mode)}
          </Button>
        </div>
      }
    >
      {children}
    </Modal>
  )
}
